import { useEffect, useState } from "react";
import { DataGrid, GridCol } from "@/components/DataGrid";
import { Drawer } from "@/pages/a/monitoring/articles/detail";
import { SyncBar } from "@/components/SyncBar";
import { Highlight } from "@/components/Highlight";
import { DateRange } from "@/components/DateRange";
import { Tile } from "@/components/Tile";

const TONE_ID: Record<string, string> = { positive: "Positif", neutral: "Netral", negative: "Negatif" };
const Tone = ({ t }: { t: { positive: number; negative: number; neutral: number } }) => (
  <span style={{ display: "grid", gap: 3, minWidth: 100 }}>
    <span className="avio-prow__track">
      {t.positive > 0 && <span className="avio-prow__fill" style={{ flex: t.positive, background: "var(--mark-pos)" }} />}
      {t.neutral > 0 && <span className="avio-prow__fill" style={{ flex: t.neutral, background: "var(--mark-neu)" }} />}
      {t.negative > 0 && <span className="avio-prow__fill" style={{ flex: t.negative, background: "var(--mark-neg)" }} />}
    </span>
    <span className="avio-sub" style={{ fontVariantNumeric: "tabular-nums" }}>
      <span style={{ color: "var(--sent-pos)" }}>+{t.positive}</span> · {t.neutral} · <span style={{ color: "var(--sent-neg)" }}>-{t.negative}</span>
    </span>
  </span>
);

export default function PersonsPage() {
  const [rows, setRows] = useState<any[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [arts, setArts] = useState<Record<string, any[]>>({});
  const [detailId, setDetailId] = useState<string | null>(null);
  const today = new Date().toISOString().slice(0, 10);
  const defStart = new Date(Date.now() + 7 * 3600e3 - 86400e3).toISOString().slice(0, 10); // default: Kemarin (H-1 WIB)
  const [start, setStart] = useState(defStart);
  const [end, setEnd] = useState(today);

  async function load(sOv?: string, eOv?: string) {
    const r = await fetch(`/api/v1/persons/top?start=${sOv ?? start}&end=${eOv ?? end}`);
    if (r.status === 401) { location.href = "/login"; return; }
    setRows((await r.json()).data); setOpen(null); setArts({});
  }
  useEffect(() => { load(); }, []);
  async function toggle(person: string) {
    if (open === person) { setOpen(null); return; }
    setOpen(person);
    if (!arts[person]) {
      const r = await fetch(`/api/v1/mentions?person=${encodeURIComponent(person)}&start=${start}&end=${end}&limit=50`);
      const j = await r.json();
      setArts((p) => ({ ...p, [person]: j.data }));
    }
  }
  const totalArt = rows.reduce((a, r) => a + (r.mentions ?? 0), 0);
  const totalNeg = rows.reduce((a, r) => a + (r.tones?.negative ?? 0), 0);
  return (
    <div className="avio-rise">
      <div style={{ display: "flex", alignItems: "flex-end", gap: "var(--space-6)", flexWrap: "wrap" }}>
        <div>
          <h1 className="avio-page-title" style={{ margin: 0 }}>Top Tokoh</h1>
          <div className="avio-t-sm avio-muted">Media massa · {start} → {end} (WIB) · klik baris untuk melihat artikelnya</div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <DateRange start={start} end={end} onApply={(s2, e2) => { setStart(s2); setEnd(e2); load(s2, e2); }} />
        </div>
      </div>
      <SyncBar wing="media" />
      <div className="avio-tiles">
        <Tile c={1} icon="fa-solid fa-user-tie" label="Tokoh terdeteksi" value={rows.length} sub="dikutip di media" />
        <Tile c={3} icon="fa-solid fa-newspaper" label="Total kutipan" value={totalArt} sub="artikel" />
        <Tile c={2} icon="fa-solid fa-circle-exclamation" label="Kutipan negatif" value={totalNeg} sub="konteks negatif" />
      </div>
      <div className="avio-tablewrap">
      <DataGrid
        cols={[
          { id: "no", header: "#", width: "max", cell: (r: any) => rows.indexOf(r) + 1 },
          { id: "tokoh", header: "Tokoh (klik = artikel)", width: "min:180", sortable: true, accessor: (r: any) => r.person, cell: (r: any) => (
            <span style={{ display: "flex", alignItems: "center", gap: "var(--space-4)" }}>
              <i className={open === r.person ? "fa-solid fa-chevron-down" : "fa-solid fa-chevron-right"} style={{ fontSize: 10, color: "var(--color-text-muted)" }} />
              <span className="avio-ava">{r.person.split(" ").slice(0, 2).map((x: string) => x[0]).join("").toUpperCase()}</span>
              <b>{r.person}</b>
            </span>) },
          { id: "artikel", header: "Artikel", width: "max", sortable: true, align: "right", accessor: (r: any) => r.mentions, cell: (r: any) => r.mentions },
          { id: "tone", header: "Komposisi tone", width: "min:120", cell: (r: any) => <Tone t={r.tones} /> },
          { id: "quote", header: "Contoh Kutipan", width: "fr", cell: (r: any) => <span style={{ color: "#444" }}>{r.sampleQuote ? `“${r.sampleQuote.slice(0, 110)}${r.sampleQuote.length > 110 ? "…" : ""}”` : ""}</span> },
        ] as GridCol<any>[]}
        data={rows}
        onRowClick={(r) => toggle(r.person)}
        expandedRender={(r) => open === r.person ? (
          !arts[r.person] ? <span>memuat…</span> : <>{arts[r.person].map((a: any) => (
            <a key={a.id} className="avio-item" href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDetailId(a.id); }} style={{ paddingLeft: 0, paddingRight: 0 }}>
              <span className="avio-item__main">
                <span className="avio-item__title"><Highlight text={a.title} extra={[r.person]} /></span>
                <span className="avio-item__meta">{new Date(new Date(a.publishedAt).getTime() + 7 * 3600e3).toISOString().slice(5, 16).replace("T", " ")}{a.mediaName ? ` · ${a.mediaName}` : ""}</span>
              </span>
              {a.sentimentLabel && <span className="avio-chip" data-sentiment={a.sentimentLabel}><span className="avio-chip__label">{TONE_ID[a.sentimentLabel] ?? a.sentimentLabel}</span></span>}
            </a>
          ))}</>
        ) : null}
        emptyText="Belum ada data narasumber pada rentang ini."
        minTableWidth={760}
      />
      </div>
      {detailId && <Drawer id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}
